/**
 * react-scan bootstrap (local development only).
 *
 * react-scan must be evaluated BEFORE React and React DOM so it can install its
 * instrumentation hook before React registers its renderer. This module is
 * therefore imported as the very first statement in index.tsx as a side-effect
 * import — the import sorter never reorders side-effect imports, so it stays
 * pinned above the React imports (a bare `import { scan } from 'react-scan'`
 * would get sorted alphabetically below `react`/`react-dom` and break this).
 *
 * It is active ONLY on local development runs (`pnpm start`/`pnpm dev`, which
 * build with NODE_ENV=development). Every production-mode build — all deployed
 * environments and Vercel previews included — strips the library entirely via the
 * matching `react-scan$` alias gate in webpack.config.js and never initializes it
 * here.
 *
 * Loaded from npm (pinned + integrity-locked in pnpm-lock.yaml) rather than an
 * external CDN, which removes the supply-chain risk of an un-pinned unpkg script
 * and lets us drop the `unpkg.com` CSP allowances entirely.
 */
import { scan } from 'react-scan';

import { useStore } from '@/store/store';

/** True only on local development builds, where react-scan is bundled and initialized. */
export const isReactScanAvailable = process.env.NODE_ENV !== 'production';

/**
 * Enable or disable the react-scan render overlay at runtime. No-op outside local
 * development, so it is safe to call anywhere.
 */
export function applyReactScanEnabled(enabled: boolean): void {
  if (!isReactScanAvailable) return;
  scan({ enabled });
}

// Initialize on load, honoring the persisted toggle state (default on in dev).
if (isReactScanAvailable) {
  applyReactScanEnabled(useStore.getState().reactScanEnabled);
}
